import React, { useRef } from "react";

import Button from "components/UI/Button";
import { DEFAULT_DEPOSIT_REFUND_POLICY, DEFAULT_PENALTY_POLICY, TOUR_POLICY_DESCRIPTIONS, TOUR_POLICY_FALLBACK_COLOR, TOUR_POLICY_LABELS, TOUR_POLICY_MAX_ITEMS, TOUR_POLICY_PERIOD_MAX_LENGTH, TOUR_POLICY_RATE_MAX_LENGTH, TOUR_REFUND_DAYS_BEFORE_MAX } from "utils/constant/tour-policy";
import { createEmptyPolicyRow, syncRefundPeriodTexts, syncRefundRateText, toPolicyFormRows } from "utils/tour-policy";

import type { TourPolicyFormRow, TourPolicyKind } from "utils/type/tour-policy";
import "./TourPolicyEditor.scss";

interface TourPolicyEditorProps {
  kind: TourPolicyKind;
  rows: TourPolicyFormRow[];
  onChange: (rows: TourPolicyFormRow[]) => void;
  /** 이 규정에 대한 검증/서버 오류 */
  error?: string | null;
  /** 원래 값에서 바뀌었는지 — 저장 대상 표시용 */
  dirty?: boolean;
}

/** 예약금 환불 규정 / 위약금 규정 항목 편집기 */
const TourPolicyEditor: React.FC<TourPolicyEditorProps> = ({ kind, rows, onChange, error, dirty }) => {
  const label = TOUR_POLICY_LABELS[kind];
  const isRefund = kind === "refund";

  const updateRow = (idx: number, patch: Partial<TourPolicyFormRow>) => onChange(rows.map((row, i) => (i === idx ? { ...row, ...patch } : row)));

  // 기준일·환불율 입력을 시작한 시점의 행. 입력 중 빈 값을 거쳐도 문구 숫자가 어긋나지 않도록
  // 매 입력마다 이 시점 문구에서 다시 계산한다 (포커스가 빠지면 비운다).
  const syncBaseRef = useRef<TourPolicyFormRow[] | null>(null);
  const beginSync = () => {
    syncBaseRef.current = rows;
  };
  const endSync = () => {
    syncBaseRef.current = null;
  };

  // 기준일이 바뀌면 이웃 구간 경계도 바뀌므로 모든 행의 기간 문구 숫자를 함께 맞춘다.
  const changeDaysBefore = (idx: number, value: string) => {
    const base = syncBaseRef.current ?? rows;
    const next = rows.map((row, i) => (i === idx ? { ...row, days_before: value } : row));
    onChange(syncRefundPeriodTexts(base, next));
  };

  const changeRatePercent = (idx: number, value: string) => {
    const base = syncBaseRef.current ?? rows;
    onChange(rows.map((row, i) => (i === idx ? syncRefundRateText(base[i] ?? row, { ...row, rate_percent: value }) : row)));
  };

  const removeRow = (idx: number) => onChange(rows.filter((_, i) => i !== idx));

  const moveRow = (idx: number, dir: -1 | 1) => {
    const target = idx + dir;
    if (target < 0 || target >= rows.length) return;
    const next = rows.slice();
    [next[idx], next[target]] = [next[target], next[idx]];
    onChange(next);
  };

  const addRow = () => {
    if (rows.length >= TOUR_POLICY_MAX_ITEMS) return;
    onChange([...rows, createEmptyPolicyRow()]);
  };

  const loadDefaults = () => {
    if (rows.length > 0 && !window.confirm(`${label}을 기본값으로 바꾸시겠습니까? 입력한 내용은 사라집니다.`)) return;
    onChange(toPolicyFormRows(isRefund ? DEFAULT_DEPOSIT_REFUND_POLICY : DEFAULT_PENALTY_POLICY));
  };

  return (
    <section className="tour-policy-editor">
      <div className="tour-policy-editor__head">
        <h4 className="tour-policy-editor__title">
          {label}
          {dirty && <span className="tour-policy-editor__dirty">변경됨</span>}
        </h4>
        <div className="tour-policy-editor__head-actions">
          <Button variant="ghost" size="sm" onClick={loadDefaults}>
            기본값 불러오기
          </Button>
          <Button variant="outline" size="sm" onClick={addRow} disabled={rows.length >= TOUR_POLICY_MAX_ITEMS}>
            + 항목 추가
          </Button>
        </div>
      </div>
      <p className="tour-policy-editor__desc">{TOUR_POLICY_DESCRIPTIONS[kind]}</p>

      {rows.length === 0 ? (
        <p className="tour-policy-editor__empty">등록된 항목이 없습니다. 최소 1개 항목이 필요합니다.</p>
      ) : (
        <div className={`tour-policy-editor__table ${isRefund ? "tour-policy-editor__table--refund" : ""}`}>
          <div className="tour-policy-editor__row tour-policy-editor__row--header">
            <span>색상</span>
            <span>기간 (표시 문구)</span>
            <span>환불 (표시 문구)</span>
            {isRefund && <span>기준일 (출발 N일 전)</span>}
            {isRefund && <span>환불율 (%)</span>}
            <span />
          </div>
          {rows.map((row, i) => {
            const no = i + 1;
            return (
              <div key={i} className="tour-policy-editor__row">
                <input type="color" className="tour-policy-editor__color" value={row.color || TOUR_POLICY_FALLBACK_COLOR} onChange={(e) => updateRow(i, { color: e.target.value })} aria-label={`${label} ${no}번째 색상`} />
                <input type="text" className="tour-policy-editor__input" value={row.period} maxLength={TOUR_POLICY_PERIOD_MAX_LENGTH} placeholder="예) 출발 30일 전까지" onChange={(e) => updateRow(i, { period: e.target.value })} aria-label={`${label} ${no}번째 기간`} />
                <input type="text" className="tour-policy-editor__input" value={row.rate} maxLength={TOUR_POLICY_RATE_MAX_LENGTH} placeholder="예) 100% / 환불 불가" onChange={(e) => updateRow(i, { rate: e.target.value })} aria-label={`${label} ${no}번째 환불 문구`} />
                {isRefund && <input type="number" className="tour-policy-editor__input" min={0} max={TOUR_REFUND_DAYS_BEFORE_MAX} step={1} value={row.days_before} placeholder="45" onFocus={beginSync} onBlur={endSync} onChange={(e) => changeDaysBefore(i, e.target.value)} aria-label={`${label} ${no}번째 기준일`} />}
                {isRefund && <input type="number" className="tour-policy-editor__input" min={0} max={100} step={1} value={row.rate_percent} placeholder="100" onFocus={beginSync} onBlur={endSync} onChange={(e) => changeRatePercent(i, e.target.value)} aria-label={`${label} ${no}번째 환불율`} />}
                <div className="tour-policy-editor__row-actions">
                  <button type="button" className="tour-policy-editor__icon-btn" onClick={() => moveRow(i, -1)} disabled={i === 0} aria-label={`${label} ${no}번째 위로`}>
                    ↑
                  </button>
                  <button type="button" className="tour-policy-editor__icon-btn" onClick={() => moveRow(i, 1)} disabled={i === rows.length - 1} aria-label={`${label} ${no}번째 아래로`}>
                    ↓
                  </button>
                  <button type="button" className="tour-policy-editor__icon-btn tour-policy-editor__icon-btn--danger" onClick={() => removeRow(i)} aria-label={`${label} ${no}번째 삭제`}>
                    ×
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {error && (
        <p className="tour-policy-editor__error" role="alert">
          {error}
        </p>
      )}
    </section>
  );
};

export default TourPolicyEditor;
